import React, { useState, useRef, useEffect } from 'react';

interface AudioPlayerProps {
  src: string;
  title?: string;
  // Playback restrictions (defaults keep the player unrestricted)
  maxPlays?: number | null;
  playsUsed?: number;
  onPlayStart?: () => void;
  allowPause?: boolean;
  allowSeek?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  title,
  maxPlays = null,
  playsUsed = 0,
  onPlayStart,
  allowPause = true,
  allowSeek = true
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  // Whether the current playthrough has started (and was counted)
  const startedRef = useRef(false);
  // Last position reached through normal playback, used to undo blocked seeks
  const lastTimeRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const playsLeft = maxPlays ? Math.max(0, maxPlays - playsUsed) : Infinity;
  const canStartNewPlay = playsLeft > 0;

  // Reset player state when src changes (new audio loaded)
  useEffect(() => {
    startedRef.current = false;
    lastTimeRef.current = 0;
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
    setIsDragging(false);
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.load();
    }
  }, [src]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      if (allowPause) audio.pause();
      return;
    }

    if (!startedRef.current) {
      if (!canStartNewPlay) return;
      audio.play().then(() => {
        startedRef.current = true;
        onPlayStart?.();
      }).catch(err => console.error('Audio playback failed:', err));
    } else {
      audio.play().catch(err => console.error('Audio playback failed:', err));
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (audio && !audio.seeking) {
      lastTimeRef.current = audio.currentTime;
    }
    if (audio && !isDragging) {
      setProgress((audio.currentTime / audio.duration) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    startedRef.current = false;
    lastTimeRef.current = 0;
    setIsPlaying(false);
    setProgress(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  // Blocks seeking from any source (keyboard, OS media controls, devtools)
  const handleSeeking = () => {
    const audio = audioRef.current;
    if (!audio || allowSeek) return;
    if (Math.abs(audio.currentTime - lastTimeRef.current) > 0.5) {
      audio.currentTime = lastTimeRef.current;
    }
  };

  // Blocks pausing from outside the player UI (e.g. OS media keys)
  const handlePause = () => {
    const audio = audioRef.current;
    if (audio && !allowPause && startedRef.current && !audio.ended && audio.isConnected) {
      audio.play().catch(() => setIsPlaying(false));
      return;
    }
    setIsPlaying(false);
  };

  const calculateSeekPosition = (clientX: number): number => {
    if (!progressRef.current || !audioRef.current) return 0;
    
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
    
    return (percentage / 100) * audioRef.current.duration;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !allowSeek) return;
    
    const seekTime = calculateSeekPosition(e.clientX);
    audioRef.current.currentTime = seekTime;
    setProgress((seekTime / audioRef.current.duration) * 100);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!allowSeek) return;
    setIsDragging(true);
    handleProgressClick(e);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !audioRef.current) return;
    
    const seekTime = calculateSeekPosition(e.clientX);
    audioRef.current.currentTime = seekTime;
    setProgress((seekTime / audioRef.current.duration) * 100);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const buttonDisabled = isPlaying ? !allowPause : (!startedRef.current && !canStartNewPlay);

  const restrictionNotes: string[] = [];
  if (maxPlays) {
    restrictionNotes.push(
      playsLeft > 0
        ? `Plays remaining: ${playsLeft} of ${maxPlays}`
        : `No plays remaining (limit: ${maxPlays})`
    );
  }
  if (!allowPause) restrictionNotes.push('Pausing is disabled once playback starts');
  if (!allowSeek) restrictionNotes.push('Skipping forward/back is disabled');

  return (
    <div className="audio-player">
      {title && <div className="audio-title">{title}</div>}
      <div className="audio-controls">
        <button
          className={`play-button ${isPlaying ? 'playing' : ''}`}
          onClick={togglePlayPause}
          disabled={buttonDisabled}
          style={buttonDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          {isPlaying ? (allowPause ? '❚❚' : '♪') : '▶'}
        </button>
        <div className="progress-container">
          <div 
            className="progress-bar"
            ref={progressRef}
            onClick={handleProgressClick}
            onMouseDown={handleMouseDown}
            style={{ cursor: allowSeek ? 'pointer' : 'default' }}
          >
            <div
              className="progress"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          {audioRef.current && (
            <div className="time">
              {formatTime(audioRef.current.currentTime)} / {formatTime(duration)}
            </div>
          )}
        </div>
      </div>
      {restrictionNotes.length > 0 && (
        <div className="audio-restrictions" style={{ fontSize: '0.85em', color: '#666', marginTop: '6px' }}>
          {restrictionNotes.join(' · ')}
        </div>
      )}
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={handlePause}
        onSeeking={handleSeeking}
      />
    </div>
  );
};

export default AudioPlayer;
